import { TwentyFour } from '@minigame-race/contracts';
import { useEffect, useState } from 'react';
import type { MinigameProps } from '../../race/types.js';
import { color } from '../../theme.js';

/**
 * Make-The-Number. The player assembles an expression by tapping number chips
 * and operators — no free typing — so every submission is well-formed and each
 * number can be used at most once locally. The server has final say.
 */

type Tok = TwentyFour.TwentyFourToken;

function isView(v: unknown): v is TwentyFour.TwentyFourPrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'twenty-four';
}

/** Render the running expression for the readout. */
function renderTokens(tokens: Tok[], numbers: readonly number[]): string {
  return tokens
    .map((t) =>
      t.t === 'num' ? String(numbers[t.i] ?? '?') : t.t === 'op' ? ` ${t.op === '*' ? '×' : t.op === '/' ? '÷' : t.op} ` : t.t === 'lparen' ? '(' : ')',
    )
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

export function TwentyFourGame({ view, emit, rejectNonce }: MinigameProps) {
  const s = isView(view) ? view : null;
  const [tokens, setTokens] = useState<Tok[]>([]);
  const [flash, setFlash] = useState(false);

  // Clear the working expression whenever the server resolves an attempt.
  useEffect(() => {
    if (s?.attempts !== undefined) setTokens([]);
  }, [s?.attempts]);

  useEffect(() => {
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 350);
    return () => clearTimeout(t);
  }, [rejectNonce, s?.attempts]);

  if (!s) return null;

  const usedIdx = new Set(tokens.filter((t): t is Extract<Tok, { t: 'num' }> => t.t === 'num').map((t) => t.i));
  const last = tokens[tokens.length - 1];
  const canNum = !last || last.t === 'op' || last.t === 'lparen';
  const canOp = last?.t === 'num' || last?.t === 'rparen';

  const push = (tok: Tok): void => setTokens((ts) => [...ts, tok]);
  const backspace = (): void => setTokens((ts) => ts.slice(0, -1));
  const clear = (): void => setTokens([]);
  const submit = (): void => {
    if (tokens.length === 0) return;
    emit({ type: 'submit', tokens });
  };

  const chip = (label: string, onClick: () => void, opts: { disabled?: boolean; kind?: 'num' | 'op' | 'ctrl' } = {}) => {
    const kind = opts.kind ?? 'ctrl';
    const bg = kind === 'num' ? 'rgba(166,255,46,.1)' : kind === 'op' ? 'rgba(34,224,255,.1)' : 'rgba(255,255,255,.05)';
    const bd = kind === 'num' ? 'rgba(166,255,46,.4)' : kind === 'op' ? 'rgba(34,224,255,.4)' : 'rgba(234,255,234,.2)';
    const fg = kind === 'num' ? color.lime : kind === 'op' ? color.cyan : 'rgba(234,255,234,.7)';
    return (
      <button
        type="button"
        disabled={opts.disabled}
        onClick={onClick}
        style={{
          minWidth: 'clamp(40px,10cqmin,58px)',
          height: 'clamp(40px,10cqmin,58px)',
          padding: '0 6px',
          borderRadius: 12,
          border: `1px solid ${bd}`,
          background: bg,
          color: fg,
          fontWeight: 700,
          fontSize: 'clamp(15px,3.2cqmin,22px)',
          fontFamily: "'JetBrains Mono',monospace",
          cursor: opts.disabled ? 'default' : 'pointer',
          opacity: opts.disabled ? 0.3 : 1,
          touchAction: 'manipulation',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ width: '100%', maxWidth: 'min(92cqmin,460px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'clamp(10px,2.4cqmin,18px)' }}>
      {/* target */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 'clamp(9px,1.7cqmin,12px)', letterSpacing: '.2em', color: 'rgba(234,255,234,.45)' }}>MAKE</div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(38px,9cqmin,68px)', lineHeight: 1, color: color.lime }}>
          {s.target}
        </div>
      </div>

      {/* working expression */}
      <div
        style={{
          minHeight: 'clamp(38px,8cqmin,52px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 12px',
          borderRadius: 12,
          border: `1px solid ${flash ? 'rgba(255,46,77,.6)' : 'rgba(166,255,46,.2)'}`,
          background: flash ? 'rgba(255,46,77,.08)' : 'rgba(255,255,255,.03)',
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: 'clamp(16px,3.6cqmin,26px)',
          fontWeight: 700,
          color: '#fff',
          transition: 'all .15s ease',
        }}
      >
        {tokens.length ? renderTokens(tokens, s.numbers) : <span style={{ color: 'rgba(234,255,234,.3)' }}>tap numbers &amp; operators</span>}
      </div>
      {s.lastError && !tokens.length && (
        <div style={{ textAlign: 'center', fontSize: 'clamp(10px,1.9cqmin,13px)', color: color.amber }}>{s.lastError}</div>
      )}

      {/* numbers */}
      <div style={{ display: 'flex', gap: 'clamp(6px,1.6cqmin,10px)', justifyContent: 'center', flexWrap: 'wrap' }}>
        {s.numbers.map((n, i) =>
          chip(String(n), () => push({ t: 'num', i }), { disabled: usedIdx.has(i) || !canNum || s.isComplete, kind: 'num' }),
        )}
      </div>

      {/* operators + parens */}
      <div style={{ display: 'flex', gap: 'clamp(6px,1.6cqmin,10px)', justifyContent: 'center', flexWrap: 'wrap' }}>
        {(['+', '-', '*', '/'] as const).map((op) =>
          chip(op === '*' ? '×' : op === '/' ? '÷' : op, () => push({ t: 'op', op }), { disabled: !canOp || s.isComplete, kind: 'op' }),
        )}
        {chip('(', () => push({ t: 'lparen' }), { disabled: !canNum || s.isComplete })}
        {chip(')', () => push({ t: 'rparen' }), { disabled: last?.t !== 'num' && last?.t !== 'rparen', kind: 'ctrl' })}
      </div>

      {/* controls */}
      <div style={{ display: 'flex', gap: 'clamp(6px,1.6cqmin,10px)', justifyContent: 'center' }}>
        {chip('⌫', backspace, { disabled: !tokens.length || s.isComplete })}
        {chip('CLR', clear, { disabled: !tokens.length || s.isComplete })}
        <button
          type="button"
          disabled={!tokens.length || s.isComplete}
          onClick={submit}
          style={{
            flex: 1,
            maxWidth: 180,
            height: 'clamp(40px,10cqmin,58px)',
            borderRadius: 12,
            border: '1px solid rgba(166,255,46,.5)',
            background: 'linear-gradient(160deg,rgba(166,255,46,.9),rgba(120,200,20,.85))',
            color: '#08110a',
            fontWeight: 800,
            fontSize: 'clamp(13px,2.8cqmin,18px)',
            letterSpacing: '.06em',
            cursor: !tokens.length || s.isComplete ? 'default' : 'pointer',
            opacity: !tokens.length || s.isComplete ? 0.35 : 1,
            touchAction: 'manipulation',
          }}
        >
          SUBMIT
        </button>
      </div>
    </div>
  );
}
